// src/services/leaveService.js
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

// Leave types
export const LEAVE_TYPES = {
  SICK: "sick",
  PERSONAL: "personal",
  VACATION: "vacation",
  EMERGENCY: "emergency",
  MATERNITY: "maternity",
  PATERNITY: "paternity",
  OTHER: "other",
};

// Leave status
export const LEAVE_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
};

const LEAVES_COLLECTION = "leaves";

// Apply for leave
export const applyForLeave = async (leaveData) => {
  try {
    const leave = {
      ...leaveData,
      status: LEAVE_STATUS.PENDING,
      appliedAt: Timestamp.now(),
      startDate: Timestamp.fromDate(new Date(leaveData.startDate)),
      endDate: Timestamp.fromDate(new Date(leaveData.endDate)),
    };

    const docRef = await addDoc(collection(db, LEAVES_COLLECTION), leave);

    toast.success("Leave application submitted successfully!");
    return { id: docRef.id, ...leave };
  } catch (error) {
    console.error("Error applying for leave:", error);
    toast.error("Failed to submit leave application");
    throw error;
  }
};

// Get leaves by user ID
export const getLeavesByUserId = async (userId) => {
  try {
    const q = query(
      collection(db, LEAVES_COLLECTION),
      where("userId", "==", userId),
      orderBy("appliedAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const leaves = [];

    querySnapshot.forEach((doc) => {
      leaves.push({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate(),
        endDate: doc.data().endDate?.toDate(),
        appliedAt: doc.data().appliedAt?.toDate(),
        reviewedAt: doc.data().reviewedAt?.toDate(),
      });
    });

    return leaves;
  } catch (error) {
    console.error("Error fetching user leaves:", error);
    toast.error("Failed to fetch leaves");
    return [];
  }
};

// Get all leaves (for admin/tutor)
export const getAllLeaves = async (status = null) => {
  try {
    let q;

    if (status) {
      q = query(
        collection(db, LEAVES_COLLECTION),
        where("status", "==", status),
        orderBy("appliedAt", "desc")
      );
    } else {
      q = query(
        collection(db, LEAVES_COLLECTION),
        orderBy("appliedAt", "desc")
      );
    }

    const querySnapshot = await getDocs(q);
    const leaves = [];

    querySnapshot.forEach((doc) => {
      leaves.push({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate(),
        endDate: doc.data().endDate?.toDate(),
        appliedAt: doc.data().appliedAt?.toDate(),
        reviewedAt: doc.data().reviewedAt?.toDate(),
      });
    });

    return leaves;
  } catch (error) {
    console.error("Error fetching all leaves:", error);
    toast.error("Failed to fetch leaves");
    return [];
  }
};

// Approve leave
export const approveLeave = async (
  leaveId,
  reviewerId,
  reviewerName,
  comments = ""
) => {
  try {
    const leaveRef = doc(db, LEAVES_COLLECTION, leaveId);

    await updateDoc(leaveRef, {
      status: LEAVE_STATUS.APPROVED,
      reviewerId,
      reviewerName,
      reviewedAt: Timestamp.now(),
      adminComments: comments,
    });

    toast.success("Leave approved successfully!");
    return true;
  } catch (error) {
    console.error("Error approving leave:", error);
    toast.error("Failed to approve leave");
    throw error;
  }
};

// Reject leave
export const rejectLeave = async (
  leaveId,
  reviewerId,
  reviewerName,
  comments = ""
) => {
  try {
    const leaveRef = doc(db, LEAVES_COLLECTION, leaveId);

    await updateDoc(leaveRef, {
      status: LEAVE_STATUS.REJECTED,
      reviewerId,
      reviewerName,
      reviewedAt: Timestamp.now(),
      adminComments: comments,
    });

    toast.success("Leave rejected!");
    return true;
  } catch (error) {
    console.error("Error rejecting leave:", error);
    toast.error("Failed to reject leave");
    throw error;
  }
};

// Cancel leave (by applicant)
export const cancelLeave = async (leaveId) => {
  try {
    const leaveRef = doc(db, LEAVES_COLLECTION, leaveId);

    await updateDoc(leaveRef, {
      status: LEAVE_STATUS.CANCELLED,
      cancelledAt: Timestamp.now(),
    });

    toast.success("Leave cancelled successfully!");
    return true;
  } catch (error) {
    console.error("Error cancelling leave:", error);
    toast.error("Failed to cancel leave");
    throw error;
  }
};

// Get leave by ID
export const getLeaveById = async (leaveId) => {
  try {
    const leaveRef = doc(db, LEAVES_COLLECTION, leaveId);
    const leaveSnap = await getDoc(leaveRef);

    if (leaveSnap.exists()) {
      const data = leaveSnap.data();
      return {
        id: leaveSnap.id,
        ...data,
        startDate: data.startDate?.toDate(),
        endDate: data.endDate?.toDate(),
        appliedAt: data.appliedAt?.toDate(),
        reviewedAt: data.reviewedAt?.toDate(),
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching leave:", error);
    toast.error("Failed to fetch leave details");
    return null;
  }
};

// Delete leave
export const deleteLeave = async (leaveId) => {
  try {
    await deleteDoc(doc(db, LEAVES_COLLECTION, leaveId));
    toast.success("Leave deleted successfully!");
    return true;
  } catch (error) {
    console.error("Error deleting leave:", error);
    toast.error("Failed to delete leave");
    throw error;
  }
};

// Calculate total leave days
export const calculateLeaveDays = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
  return diffDays;
};

// Check if user has approved leave for a specific date
export const hasApprovedLeaveForDate = async (userId, date) => {
  try {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const q = query(
      collection(db, LEAVES_COLLECTION),
      where("userId", "==", userId),
      where("status", "==", LEAVE_STATUS.APPROVED)
    );

    const querySnapshot = await getDocs(q);
    
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      const startDate = data.startDate?.toDate();
      const endDate = data.endDate?.toDate();
      
      if (startDate && endDate) {
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        
        // Check if target date falls within the leave period
        if (targetDate >= startDate && targetDate <= endDate) {
          return {
            hasLeave: true,
            leaveData: {
              id: doc.id,
              ...data,
              startDate: data.startDate?.toDate(),
              endDate: data.endDate?.toDate(),
              appliedAt: data.appliedAt?.toDate(),
              reviewedAt: data.reviewedAt?.toDate(),
            }
          };
        }
      }
    }
    
    return { hasLeave: false, leaveData: null };
  } catch (error) {
    console.error("Error checking leave for date:", error);
    return { hasLeave: false, leaveData: null };
  }
};

// Get all users with approved leave for a specific date
export const getUsersWithApprovedLeaveForDate = async (date) => {
  try {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    
    const q = query(
      collection(db, LEAVES_COLLECTION),
      where("status", "==", LEAVE_STATUS.APPROVED)
    );

    const querySnapshot = await getDocs(q);
    const usersOnLeave = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const startDate = data.startDate?.toDate();
      const endDate = data.endDate?.toDate();
      
      if (startDate && endDate) {
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        
        if (targetDate >= startDate && targetDate <= endDate) {
          usersOnLeave.push({
            userId: data.userId,
            userName: data.userName,
            leaveType: data.leaveType,
            reason: data.reason,
            leaveId: doc.id,
            startDate: data.startDate?.toDate(),
            endDate: data.endDate?.toDate(),
          });
        }
      }
    });
    
    return usersOnLeave;
  } catch (error) {
    console.error("Error getting users with leave for date:", error);
    return [];
  }
};

// Get leave statistics for a user
export const getLeaveStats = async (userId) => {
  try {
    const leaves = await getLeavesByUserId(userId);

    const stats = {
      total: leaves.length,
      pending: leaves.filter((l) => l.status === LEAVE_STATUS.PENDING).length,
      approved: leaves.filter((l) => l.status === LEAVE_STATUS.APPROVED).length,
      rejected: leaves.filter((l) => l.status === LEAVE_STATUS.REJECTED).length,
      totalDaysRequested: 0,
      totalDaysApproved: 0,
    };

    leaves.forEach((leave) => {
      const days = calculateLeaveDays(leave.startDate, leave.endDate);
      stats.totalDaysRequested += days;

      if (leave.status === LEAVE_STATUS.APPROVED) {
        stats.totalDaysApproved += days;
      }
    });

    return stats;
  } catch (error) {
    console.error("Error calculating leave stats:", error);
    return null;
  }
};

// Get approved leaves for a specific month
export const getApprovedLeavesForMonth = async (year, month) => {
  try {
    // Get first and last day of month
    const firstDay = new Date(year, month - 1, 1).toISOString().split("T")[0];
    const lastDay = new Date(year, month, 0).toISOString().split("T")[0];

    const leaves = await getAllLeaves(LEAVE_STATUS.APPROVED);

    // Filter leaves that overlap with the month
    const monthLeaves = leaves.filter((leave) => {
      const leaveStart = leave.startDate;
      const leaveEnd = leave.endDate;

      // Check if leave overlaps with the month
      return leaveStart <= lastDay && leaveEnd >= firstDay;
    });

    return monthLeaves;
  } catch (error) {
    console.error("Error getting approved leaves for month:", error);
    return [];
  }
};
